import React from 'react';
import { SmartphoneIcon, TabletIcon, MonitorIcon, HomeIcon, LockIcon, ShieldIcon, ExternalLinkIcon } from 'lucide-react';
export const LockScreen = () => {
  return <div className="flex-1 flex flex-col bg-[#FFFBF6] overflow-hidden" data-id="element-1357">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-[#E5C1A9] p-4" data-id="element-1358">
        <div className="max-w-7xl mx-auto" data-id="element-1359">
          <h1 className="text-2xl font-bold text-[#6B4B3E]" data-id="element-1360">
            Parental Controls
          </h1>
          <p className="text-sm text-[#6B4B3E]/75" data-id="element-1361">
            Simple steps to lock your screen
          </p>
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-auto" data-id="element-1362">
        <div className="max-w-3xl mx-auto px-4 py-8" data-id="element-1363">
          {/* Introduction */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-1364">
            <p className="text-[#6B4B3E]/80 text-lg leading-relaxed" data-id="element-1365">
              Story Bunny runs in your browser — not as an app — which means it
              works on any device without needing to download anything. Here are
              quick ways to keep www.storybunny.ai locked on your device.
            </p>
          </div>
          {/* Safety Note */}
          <div className="bg-[#FFF0E6] rounded-xl p-6 mb-8 flex items-start space-x-4" data-id="element-1366">
            <div className="text-2xl flex-shrink-0" data-id="element-1367">✨</div>
            <p className="text-[#6B4B3E]/80" data-id="element-1368">
              Story Bunny doesn't use pop-up ads or external links — everything
              stays within the story environment.
            </p>
          </div>
          {/* Shortcut Tip */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-1369">
            <h2 className="text-xl font-bold text-[#6B4B3E] mb-4 flex items-center" data-id="element-1370">
              <HomeIcon className="mr-2" data-id="element-1371" />
              Create a Shortcut
            </h2>
            <p className="text-[#6B4B3E]/80 mb-4" data-id="element-1372">
              Start Story Bunny straight from your Home Screen:
            </p>
            <ul className="space-y-2 text-[#6B4B3E]/80" data-id="element-1373">
              <li data-id="element-1374">
                On Safari (iOS): Tap{' '}
                <span className="font-medium" data-id="element-1375">Share icon</span> →{' '}
                <span className="font-medium" data-id="element-1376">Add to Home Screen</span>
              </li>
              <li data-id="element-1377">
                On Chrome (Android): Tap{' '}
                <span className="font-medium" data-id="element-1378">More</span> →{' '}
                <span className="font-medium" data-id="element-1379">Add to Home Screen</span>
              </li>
            </ul>
          </div>
          {/* iOS Instructions */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-1380">
            <h2 className="text-xl font-bold text-[#6B4B3E] mb-4 flex items-center" data-id="element-1381">
              <TabletIcon className="mr-2" data-id="element-1382" />
              iPad or iPhone Instructions
            </h2>
            <div className="mb-6" data-id="element-1383">
              <h3 className="font-bold text-[#6B4B3E] mb-3" data-id="element-1384">
                Step 1: Restrict Web Access to Only Story Bunny
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-[#6B4B3E]/80" data-id="element-1385">
                <li data-id="element-1386">
                  Go to <span className="font-medium" data-id="element-1387">Settings</span> →{' '}
                  <span className="font-medium" data-id="element-1388">Screen Time</span> →{' '}
                  <span className="font-medium" data-id="element-1389">
                    Content & Privacy Restrictions
                  </span>
                </li>
                <li data-id="element-1390">
                  Tap <span className="font-medium" data-id="element-1391">Content Restrictions</span>
                </li>
                <li data-id="element-1392">
                  Tap <span className="font-medium" data-id="element-1393">Web Content</span> → Select{' '}
                  <span className="font-medium" data-id="element-1394">Allowed Websites Only</span>
                </li>
                <li data-id="element-1395">Remove everything except www.storybunny.ai</li>
                <li data-id="element-1396">
                  Tap <span className="font-medium" data-id="element-1397">Add Website</span> → Type:
                  www.storybunny.ai
                </li>
              </ol>
              <p className="mt-2 text-[#6B4B3E]/80 italic" data-id="element-1398">
                This ensures your child can only access Story Bunny in Safari.
              </p>
            </div>
            <div data-id="element-1399">
              <h3 className="font-bold text-[#6B4B3E] mb-3" data-id="element-1400">
                Step 2: Turn on Guided Access
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-[#6B4B3E]/80" data-id="element-1401">
                <li data-id="element-1402">
                  Go to <span className="font-medium" data-id="element-1403">Settings</span> →{' '}
                  <span className="font-medium" data-id="element-1404">Accessibility</span> →{' '}
                  <span className="font-medium" data-id="element-1405">Guided Access</span>
                </li>
                <li data-id="element-1406">
                  Toggle <span className="font-medium" data-id="element-1407">Guided Access</span> ON
                </li>
                <li data-id="element-1408">
                  Tap <span className="font-medium" data-id="element-1409">Passcode Settings</span> →
                  Set a 4-digit code
                </li>
                <li data-id="element-1410">Open Safari and go to www.storybunny.ai</li>
                <li data-id="element-1411">
                  Triple-click the <span className="font-medium" data-id="element-1412">side</span> or{' '}
                  <span className="font-medium" data-id="element-1413">home button</span>
                </li>
                <li data-id="element-1414">
                  Tap <span className="font-medium" data-id="element-1415">Start</span> to begin
                  session
                </li>
              </ol>
              <p className="mt-2 text-[#6B4B3E]/80" data-id="element-1416">
                To exit: Triple-click again and enter your passcode
              </p>
            </div>
          </div>
          {/* Android Instructions */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-1417">
            <h2 className="text-xl font-bold text-[#6B4B3E] mb-4 flex items-center" data-id="element-1418">
              <SmartphoneIcon className="mr-2" data-id="element-1419" />
              Android Tablets or Phones
            </h2>
            <p className="text-[#6B4B3E]/80 mb-4" data-id="element-1420">
              Most Android devices have{' '}
              <span className="font-medium" data-id="element-1421">Screen Pinning</span>:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-[#6B4B3E]/80" data-id="element-1422">
              <li data-id="element-1423">
                Open <span className="font-medium" data-id="element-1424">Settings</span> →{' '}
                <span className="font-medium" data-id="element-1425">Security</span> →{' '}
                <span className="font-medium" data-id="element-1426">Screen Pinning</span> (or Search
                "Pin app")
              </li>
              <li data-id="element-1427">Turn it ON and enable a passcode</li>
              <li data-id="element-1428">Open Story Bunny in Chrome</li>
              <li data-id="element-1429">
                Tap <span className="font-medium" data-id="element-1430">Overview</span> button (square
                icon)
              </li>
              <li data-id="element-1431">Tap the pin icon on the app window</li>
            </ol>
            <p className="mt-2 text-[#6B4B3E]/80" data-id="element-1432">
              To unpin: Hold down{' '}
              <span className="font-medium" data-id="element-1433">Back + Overview</span> buttons
              together
            </p>
          </div>
          {/* Desktop Instructions */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-1434">
            <h2 className="text-xl font-bold text-[#6B4B3E] mb-4 flex items-center" data-id="element-1435">
              <MonitorIcon className="mr-2" data-id="element-1436" />
              Mac or PC
            </h2>
            <p className="text-[#6B4B3E]/80 mb-4" data-id="element-1437">
              While there's no full lock feature for browsers, you can:
            </p>
            <ul className="space-y-2 text-[#6B4B3E]/80" data-id="element-1438">
              <li data-id="element-1439">
                • Switch to{' '}
                <span className="font-medium" data-id="element-1440">Full Screen Mode</span> (⌘ +
                Control + F on Mac, or F11 on Windows)
              </li>
              <li data-id="element-1441">
                • Temporarily{' '}
                <span className="font-medium" data-id="element-1442">disconnect keyboard/mouse</span>{' '}
                for toddlers
              </li>
              <li data-id="element-1443">
                • Use <span className="font-medium" data-id="element-1444">parental controls</span> to
                restrict access to other tabs or apps
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>;
};